import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { State } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridPageChangeEvent } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { MLRunsDto } from 'api/Wallet/Types/MLRunsDto'
import { Modal } from 'components/CustomModal'
import { Loading } from 'components/Loading'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'

import { MLRunsColumns } from './MLRunsColumns'

interface MLRunsProps {
  masterTransactionId: string
  mlStatus: NameDto
  badgeVariant: 'green' | 'red' | 'orange' | 'neutral'
}

const createDataState = (state: State, data: MLRunsDto[]) => {
  const sortedData = [...data].sort(
    (a, b) => new Date(b.modelRunTimestamp).getTime() - new Date(a.modelRunTimestamp).getTime()
  )

  return {
    result: sortedData.slice(state.skip, (state.take as number) + (state.skip as number)),
    dataState: state,
  }
}

const MLRuns: React.FC<MLRunsProps> = ({ masterTransactionId, mlStatus, badgeVariant }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [dataState, setDataState] = useState<{
    result: MLRunsDto[]
    dataState: State
  }>(createDataState(getPaginationInitialState(), []))

  const { callback: fetchData } = useCallbackWithForceRefresh(
    () =>
      isModalOpen ? walletsApiClient.getMlRuns(masterTransactionId) : Promise.resolve(undefined),
    [masterTransactionId, isModalOpen]
  )
  const { data: mlRuns, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    setDataState(createDataState(dataState.dataState, mlRuns || []))
  }, [mlRuns, dataState.dataState])

  const pageChange = (event: GridPageChangeEvent) => {
    if (!mlRuns) return
    setDataState(
      createDataState(
        {
          take: event.page.take,
          skip: event.page.skip,
        },
        mlRuns
      )
    )
  }

  return (
    <>
      {isModalOpen && (
        <Modal
          title='ML Output Details'
          visible={isModalOpen}
          width={1200}
          onClose={() => setIsModalOpen(false)}
        >
          <Loading isLoading={isLoading}>
            <Grid
              onPageChange={pageChange}
              take={dataState.dataState.take}
              skip={dataState.dataState.skip}
              total={mlRuns?.length}
              pageSize={dataState.dataState.take}
              data={dataState.result}
              resizable
              pageable={pageableSettings}
            >
              {MLRunsColumns.filter((c) => c.show !== false).map((column, idx) => (
                <Column key={idx} {...column} />
              ))}
            </Grid>
          </Loading>
        </Modal>
      )}
      <Badge onClick={() => setIsModalOpen(true)} className='has-pointer' variant={badgeVariant}>
        {mlStatus.name}
      </Badge>
    </>
  )
}

export default MLRuns
